import { animate, stagger } from "animejs";
import { useEffect, useRef } from "react";

type AnimationType =
  | "fadeUp"
  | "fadeDown"
  | "fadeLeft"
  | "fadeRight"
  | "scaleUp"
  | "staggerUp"
  | "staggerLeft"
  | "staggerScale";

interface UseScrollAnimationOptions {
  type?: AnimationType;
  delay?: number;
  duration?: number;
  threshold?: number;
  staggerDelay?: number;
}

export function useScrollAnimation<T extends HTMLElement>(
  options: UseScrollAnimationOptions = {},
) {
  const ref = useRef<T>(null);
  const hasAnimated = useRef(false);

  const {
    type = "fadeUp",
    delay = 0,
    duration = 800,
    threshold = 0.1,
    staggerDelay = 100,
  } = options;

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Set initial state based on animation type
    if (type.startsWith("stagger")) {
      Array.from(element.children).forEach((child) => {
        const el = child as HTMLElement;
        el.style.opacity = "0";
        if (type === "staggerUp") el.style.transform = "translateY(40px)";
        if (type === "staggerLeft") el.style.transform = "translateX(40px)";
        if (type === "staggerScale") el.style.transform = "scale(0.8)";
      });
    } else {
      element.style.opacity = "0";
      switch (type) {
        case "fadeUp":
          element.style.transform = "translateY(60px)";
          break;
        case "fadeDown":
          element.style.transform = "translateY(-60px)";
          break;
        case "fadeLeft":
          element.style.transform = "translateX(60px)";
          break;
        case "fadeRight":
          element.style.transform = "translateX(-60px)";
          break;
        case "scaleUp":
          element.style.transform = "scale(0.8)";
          break;
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true;

            switch (type) {
              case "fadeUp":
                animate(element, {
                  opacity: [0, 1],
                  translateY: [60, 0],
                  duration,
                  delay,
                  ease: "outExpo",
                });
                break;
              case "fadeDown":
                animate(element, {
                  opacity: [0, 1],
                  translateY: [-60, 0],
                  duration,
                  delay,
                  ease: "outExpo",
                });
                break;
              case "fadeLeft":
                animate(element, {
                  opacity: [0, 1],
                  translateX: [60, 0],
                  duration,
                  delay,
                  ease: "outExpo",
                });
                break;
              case "fadeRight":
                animate(element, {
                  opacity: [0, 1],
                  translateX: [-60, 0],
                  duration,
                  delay,
                  ease: "outExpo",
                });
                break;
              case "scaleUp":
                animate(element, {
                  opacity: [0, 1],
                  scale: [0.8, 1],
                  duration,
                  delay,
                  ease: "outBack",
                });
                break;
              case "staggerUp":
                animate(element.children, {
                  opacity: [0, 1],
                  translateY: [40, 0],
                  duration,
                  delay: stagger(staggerDelay, { start: delay }),
                  ease: "outExpo",
                });
                break;
              case "staggerLeft":
                animate(element.children, {
                  opacity: [0, 1],
                  translateX: [40, 0],
                  duration,
                  delay: stagger(staggerDelay, { start: delay }),
                  ease: "outExpo",
                });
                break;
              case "staggerScale":
                animate(element.children, {
                  opacity: [0, 1],
                  scale: [0.8, 1],
                  duration,
                  delay: stagger(staggerDelay, { start: delay }),
                  ease: "outBack",
                });
                break;
            }
          }
        });
      },
      { threshold },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [type, delay, duration, threshold, staggerDelay]);

  return ref;
}

// Simpler hook for basic fade up animation
export function useFadeUp<T extends HTMLElement>(delay = 0) {
  return useScrollAnimation<T>({ type: "fadeUp", delay });
}

// Hook for staggered children animations
export function useStaggerAnimation<T extends HTMLElement>(
  direction: "up" | "left" | "scale" = "up",
  staggerDelay = 100,
) {
  const type: AnimationType =
    direction === "up"
      ? "staggerUp"
      : direction === "left"
        ? "staggerLeft"
        : "staggerScale";
  return useScrollAnimation<T>({ type, staggerDelay });
}
